import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { ScenarioScope, scopeBase, scopeKey, useScenarioScope } from '../scope';
import { ScenarioCommit, ScenarioCreateResult, ScenarioDetail } from '../types';

type ScenarioUpdateBody = {
  label?: string;
  isPrimary?: boolean;
  archived?: boolean;
};

type ApplyCommitBody = {
  patches: Record<string, unknown>[];
  intent: string;
  rationale?: string;
  expectedLastSeq: number;
};

type CreateScenarioBody = {
  label?: string;
  // Family scope only: the one or two adult members to include.
  memberClientIds?: string[];
};

export function useScenarioMutations(scenarioId: string | undefined) {
  const scope = useScenarioScope();
  const queryClient = useQueryClient();

  // Queries guard themselves with `enabled`; mutations must fail loudly
  // rather than PATCH ".../undefined".
  const requireScenarioId = (): string => {
    if (!scenarioId) throw new Error('No cashflow model selected');
    return scenarioId;
  };
  const requireScope = (): ScenarioScope => {
    if (!scope) throw new Error('No cashflow model scope available');
    return scope;
  };

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: scope ? scopeKey(scope) : ['clients'] });

  const { mutateAsync: updateScenario } = useMutation({
    mutationFn: async (body: ScenarioUpdateBody) => {
      const { data } = await api.patch<ScenarioDetail>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const { mutateAsync: applyCommit } = useMutation({
    mutationFn: async (body: ApplyCommitBody) => {
      const { data } = await api.post<ScenarioCommit>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}/commits`,
        body,
      );
      return data;
    },
    onSuccess: invalidate,
  });

  const { mutateAsync: revertCommit } = useMutation({
    mutationFn: async ({ seq, expectedLastSeq }: { seq: number; expectedLastSeq: number }) => {
      const { data } = await api.post<ScenarioCommit>(
        `${scopeBase(requireScope())}/cashflow-scenarios/${requireScenarioId()}/commits/${seq}/revert`,
        { expectedLastSeq },
      );
      return data;
    },
    onSuccess: invalidate,
  });

  return { updateScenario, applyCommit, revertCommit };
}

export function useCreateScenario(scopeOverride?: ScenarioScope) {
  const contextScope = useScenarioScope();
  const queryClient = useQueryClient();
  const scope = scopeOverride ?? contextScope;

  const { mutateAsync: createScenario, isPending: isCreating } = useMutation({
    mutationFn: async (body: CreateScenarioBody) => {
      if (!scope) throw new Error('No cashflow model scope available');
      const { data } = await api.post<ScenarioCreateResult>(
        `${scopeBase(scope)}/cashflow-scenarios`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      if (scope) {
        queryClient.invalidateQueries({ queryKey: scopeKey(scope) });
      }
      // A family model also appears on each included member's client page.
      if (scope?.kind === 'family') {
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      }
    },
  });

  return { createScenario, isCreating };
}
